import React, { useState, useEffect, useContext } from 'react';
import { Container, Button, Row, Col, Alert, Table, Form, Card, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Customer.css';

const CustomerRelations = () => {
  const { custCode } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // 客戶資料
  const [customer, setCustomer] = useState(null);
  
  // 已關聯的用戶
  const [relations, setRelations] = useState([]);
  
  // 所有可用用戶
  const [allUsers, setAllUsers] = useState([]);
  
  // 選中的用戶ID
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // 確認刪除關聯的模態框狀態
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingLoginId, setDeletingLoginId] = useState(null);
  
  // 頁面狀態
  const [loading, setLoading] = useState(true);
  const [addingRelation, setAddingRelation] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 確保只有管理員可以訪問
  useEffect(() => {
    if (user?.role !== 0) {
      navigate('/');
    }
  }, [user, navigate]);
  
  // 載入客戶資料和相關用戶
  useEffect(() => {
    if (custCode && user?.role === 0) {
      loadCustomerAndRelations();
      loadAllUsers();
    }
  }, [custCode, user]);
  
  const loadCustomerAndRelations = async () => {
    setLoading(true);
    try {
      // 載入客戶資料
      const customerResponse = await api.get(`/customers/${custCode}`);
      setCustomer(customerResponse.data.data.customer);
      
      // 載入用戶關聯
      const relationsResponse = await api.get(`/customers/relations/customer/${custCode}`);
      setRelations(relationsResponse.data.data || []);
      
      setError('');
    } catch (err) {
      console.error('載入資料失敗:', err);
      setError('載入資料失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setLoading(false);
    }
  };
  
  const loadAllUsers = async () => {
    try {
      const response = await api.get('/users');
      setAllUsers(response.data.data || []);
    } catch (err) {
      console.error('載入用戶列表失敗:', err);
    }
  };
  
  const handleAddRelation = async () => {
    if (!selectedUserId) {
      setError('請選擇用戶');
      return;
    }
    
    setAddingRelation(true);
    setError('');
    setSuccess('');
    
    try {
      // 獲取選中用戶的login_id
      const selectedUser = allUsers.find(u => u.uid.toString() === selectedUserId);
      
      if (!selectedUser) {
        throw new Error('找不到選中的用戶');
      }
      
      await api.post('/customers/relations', {
        login_id: selectedUser.login_id,
        cust_code: custCode
      });
      
      setSuccess('用戶關聯已成功添加');
      setSelectedUserId(''); // 重置選擇
      
      // 重新載入關聯
      loadCustomerAndRelations();
    } catch (err) {
      console.error('添加關聯失敗:', err);
      setError('添加關聯失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setAddingRelation(false);
    }
  };
  
  // 打開確認刪除模態框
  const confirmDeleteRelation = (loginId) => {
    setDeletingLoginId(loginId);
    setShowDeleteModal(true);
  };
  
  // 執行刪除關聯
  const executeDeleteRelation = async () => {
    if (!deletingLoginId) return;
    
    try {
      await api.delete(`/customers/relations/${deletingLoginId}/${custCode}`);
      
      setSuccess('用戶關聯已成功刪除');
      
      // 重新載入關聯
      loadCustomerAndRelations();
    } catch (err) {
      console.error('刪除關聯失敗:', err);
      setError('刪除關聯失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      // 關閉模態框並清除刪除的登錄ID
      setShowDeleteModal(false);
      setDeletingLoginId(null);
    }
  };
  
  const handleBack = () => {
    navigate('/customers');
  };
  
  // 過濾已關聯的用戶
  const getAvailableUsers = () => {
    const relatedLoginIds = relations.map(r => r.login_id);
    return allUsers.filter(user => !relatedLoginIds.includes(user.login_id));
  };
  
  return (
    <Container className="customer-relations-container">
      <Row className="mb-3 align-items-center">
        <Col xs={8}>
          <h2>客戶與用戶關聯</h2>
          {customer && <p>客戶: {customer.cust_name} ({customer.cust_code})</p>}
        </Col>
        <Col xs={4} className="text-end">
          <Button variant="outline-secondary" onClick={handleBack}>
            返回客戶列表
          </Button>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : (
        <>
          <Card className="mb-4">
            <Card.Header>新增用戶關聯</Card.Header>
            <Card.Body>
              <Row className="align-items-end">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>選擇用戶</Form.Label>
                    <Form.Select 
                      value={selectedUserId} 
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      <option value="">請選擇用戶</option>
                      {getAvailableUsers().map(user => (
                        <option key={user.uid} value={user.uid}>
                          {user.login_id} - {user.role === 0 ? '管理員' : '客戶'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Button 
                    variant="primary" 
                    onClick={handleAddRelation}
                    disabled={addingRelation || !selectedUserId}
                    className="w-100"
                  >
                    {addingRelation ? '添加中...' : '添加關聯'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          <Card>
            <Card.Header>已關聯用戶</Card.Header>
            <Card.Body>
              {relations.length === 0 ? (
                <p className="text-center">尚未關聯任何用戶</p>
              ) : (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>用戶ID</th>
                      <th>關聯日期</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relations.map((relation, index) => (
                      <tr key={index}>
                        <td>{relation.login_id}</td>
                        <td>{new Date(relation.created_at).toLocaleDateString('zh-TW')}</td>
                        <td>
                          <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={() => confirmDeleteRelation(relation.login_id)}
                          >
                            刪除關聯
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
          
          {/* 確認刪除模態框 */}
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>確認刪除</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              確定要刪除此用戶關聯嗎？此操作無法撤銷。
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                取消
              </Button>
              <Button variant="danger" onClick={executeDeleteRelation}>
                確認刪除
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default CustomerRelations;